import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { AdminImage } from './admin-images.service';

export interface UserSession {
  id: number;
  name: string;
  created_at: string;
  product_count: number;
  visibility?: 'private' | 'restricted' | 'public';
  is_public?: boolean;
  shared_with_count?: number;
}

export interface SessionProductsQueryParams {
  limit?: number;
  offset?: number;
  brand?: string;
  item?: string;
}

export interface SessionProduct extends AdminImage {
  uploaded_by: number | null;
  uploaded_by_username: string | null;
  can_edit: boolean;
  can_delete: boolean;
}

export interface SessionProductsResponse {
  products: SessionProduct[];
  total: number;
  limit: number;
  offset: number;
}

export interface SharedSession {
  id: number;
  name: string;
  created_at: string;
  product_count: number;
  owner_id: number;
  owner_username: string;
  is_owner: boolean;
  visibility: 'restricted' | 'public';
  access_source: 'owner' | 'direct' | 'public';
  shared_with_count: number;
  can_write: boolean;
  is_public: boolean;
  can_activate: boolean;
  can_manage_sharing: boolean;
  can_delete_session: boolean;
  can_edit_all_products: boolean;
}

export interface SessionGrant {
  id: number;
  granted_to: number | null;
  granted_to_username: string | null;
  granted_to_email: string | null;
  can_write: boolean;
  is_public: boolean;
  created_at: string;
}

export interface ShareSessionRequest {
  usernames?: string[];
  public?: boolean;
  can_write?: boolean;
}

export interface ShareSessionResponse {
  message: string;
  grants_created: number;
  not_found: string[];
}

@Injectable({
  providedIn: 'root'
})
export class UserSessionsService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  private get jsonOptions() {
    return {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      withCredentials: true
    };
  }

  /** GET /sessions/user-sessions — returns the list of sessions for the current user */
  getSessions(): Observable<UserSession[]> {
    return this.http.get<UserSession[]>(
      `${this.apiUrl}/sessions/user-sessions`,
      this.jsonOptions
    );
  }

  /** POST /sessions/create — creates a new named session */
  createSession(name: string): Observable<UserSession> {
    return this.http.post<UserSession>(
      `${this.apiUrl}/sessions/create`,
      { name },
      this.jsonOptions
    );
  }

  /** DELETE /sessions/{id} — deletes the session (not its underlying products) */
  deleteSession(sessionId: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(
      `${this.apiUrl}/sessions/${sessionId}`,
      this.jsonOptions
    );
  }

  /** GET /sessions/{id}/products — returns paginated products in the session */
  getSessionProducts(sessionId: number, query: SessionProductsQueryParams): Observable<SessionProductsResponse> {
    let params = new HttpParams();
    if (query.limit !== undefined) params = params.set('limit', query.limit.toString());
    if (query.offset !== undefined) params = params.set('offset', query.offset.toString());
    if (query.brand) params = params.set('brand', query.brand);
    if (query.item) params = params.set('item', query.item);

    return this.http.get<SessionProductsResponse>(
      `${this.apiUrl}/sessions/${sessionId}/products`,
      { ...this.jsonOptions, params }
    );
  }

  /** GET /sessions/active — returns the currently active session for the user, or null */
  getActiveSession(): Observable<{ session: UserSession | null }> {
    return this.http.get<{ session: UserSession | null }>(
      `${this.apiUrl}/sessions/active`,
      this.jsonOptions
    );
  }

  /** POST /sessions/set-active — marks a session as the active one for upcoming product uploads */
  setActiveSession(sessionId: number): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.apiUrl}/sessions/set-active`,
      { session_id: sessionId },
      this.jsonOptions
    );
  }

  /** POST /sessions/clear-active — clears the active session selection */
  clearActiveSession(): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.apiUrl}/sessions/clear-active`,
      {},
      this.jsonOptions
    );
  }

  /** POST /sessions/{id}/products — adds an estimated product to the session (duplicate entry) */
  addProductToSession(
    sessionId: number,
    product: {
      brand: string;
      item: string;
      material: string;
      price: string;
      color?: string;
      product_age?: string;
      product_condition?: string;
      image_name?: string;
    }
  ): Observable<{ message: string; id: number }> {
    return this.http.post<{ message: string; id: number }>(
      `${this.apiUrl}/sessions/${sessionId}/products`,
      product,
      this.jsonOptions
    );
  }

  /** DELETE /sessions/{sessionId}/products/{productId} — removes a product from the session */
  removeProductFromSession(sessionId: number, productId: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(
      `${this.apiUrl}/sessions/${sessionId}/products/${productId}`,
      this.jsonOptions
    );
  }

  /** PATCH /sessions/products/{userProductId} — updates the details of a product in a session */
  editProduct(userProductId: number, details: {
    brand: string; item: string; material: string; color: string;
    price: number; age: string; condition: string;
  }): Observable<{ message: string; product: AdminImage }> {
    return this.http.patch<{ message: string; product: AdminImage }>(
      `${this.apiUrl}/sessions/products/${userProductId}`,
      details,
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }), withCredentials: true }
    );
  }

  /** GET /sessions/{id}/download — returns a CSV blob for the session */
  downloadSessionCsv(sessionId: number): Observable<Blob> {
    return this.http.get(
      `${this.apiUrl}/sessions/${sessionId}/download`,
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }), responseType: 'blob', withCredentials: true }
    );
  }

  /** GET /sessions/products/{id}/download-url — presigned S3 URL for a session product image */
  getSessionProductDownloadUrl(userProductId: number): Observable<{ download_url: string; expires_in: number; image_name: string }> {
    return this.http.get<{ download_url: string; expires_in: number; image_name: string }>(
      `${this.apiUrl}/sessions/products/${userProductId}/download-url`,
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }), withCredentials: true }
    );
  }

  /**
   * GET /sessions/shared-with-me — collaborative session workspace:
   * owned sessions that were shared, sessions shared directly with the user,
   * and public sessions.
   */
  getSharedSessions(): Observable<SharedSession[]> {
    return this.http.get<SharedSession[]>(
      `${this.apiUrl}/sessions/shared-with-me`,
      this.jsonOptions
    );
  }

  /** GET /sessions/{id}/grants — returns current access grants for a session (owner only) */
  getSessionGrants(sessionId: number): Observable<SessionGrant[]> {
    return this.http.get<SessionGrant[]>(
      `${this.apiUrl}/sessions/${sessionId}/grants`,
      this.jsonOptions
    );
  }

  /** POST /sessions/{id}/share — share with specific users or make public */
  shareSession(sessionId: number, payload: ShareSessionRequest): Observable<ShareSessionResponse> {
    return this.http.post<ShareSessionResponse>(
      `${this.apiUrl}/sessions/${sessionId}/share`,
      payload,
      this.jsonOptions
    );
  }

  /** DELETE /sessions/{id}/share/{username} — revoke a grant; pass 'public' to revoke public grant */
  revokeSessionGrant(sessionId: number, username: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(
      `${this.apiUrl}/sessions/${sessionId}/share/${encodeURIComponent(username)}`,
      this.jsonOptions
    );
  }
}
